import React, { useCallback, useMemo } from 'react';
import { Box } from '@televet/kibble-ui/build/chakra';
import { MenuItemProps } from '@televet/kibble-ui/build/components/Menu';
import { TagBlock } from '@televet/kibble-ui/build/patterns/TagBlock';
import { useGetAppointmentModalAutomationsQuery } from 'shared/types/graphql';
import { useAppDispatch, useAppSelector } from 'state/hooks';
import { updateAppointmentDraft, updateAppointmentModal } from 'pages/Appointments/state/appointmentsModalSlice';
import * as Sentry from '@sentry/react';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import useClinicUser from 'shared/hooks/useClinicUser';
import { Text } from '@televet/kibble-ui';

interface AppointmentAutomationsProps {
  isLoadingLinkedAutomations: boolean;
}

export const AppointmentAutomations = ({ isLoadingLinkedAutomations }: AppointmentAutomationsProps): JSX.Element => {
  const { currentClinicId } = useClinicUser();
  const { selectedAutomationIds } = useAppSelector((state) => state.appointmentsModal.appointmentDraft);
  const dispatch = useAppDispatch();
  const toast = useToast();

  const { data: appointmentAutomationsData } = useGetAppointmentModalAutomationsQuery({
    variables: { clinicId: currentClinicId || '' },
    skip: !currentClinicId,
    onError: (e) => {
      toast({
        title: 'Sorry!',
        description: 'We are having trouble looking automations for this appointment.',
        status: 'error',
      });
      Sentry.captureException(e);
    },
    onCompleted: (data) => {
      if (!data) return;
      // Setting the survey automations in state so that they can be appended to the appointment on save
      dispatch(
        updateAppointmentModal({
          surveyAutomationIds:
            data.automations?.filter((automation) => !!automation.surveySettingId).map((automation) => automation.id) ||
            [],
        }),
      );
    },
  });

  const automationOptions = useMemo(
    () =>
      appointmentAutomationsData?.automations
        // We do not show survey automations as options in this list
        ?.filter((automation) => !automation.surveySettingId)
        .map((automation) => ({
          label: automation.name,
          value: automation.id,
          isSelected: selectedAutomationIds?.includes(automation.id),
        })),
    [selectedAutomationIds, appointmentAutomationsData?.automations],
  );

  const selectedAutomationTags = useMemo(
    () =>
      automationOptions?.filter((item) => item.isSelected).map((item) => ({ label: item.label, value: item.value })),
    [automationOptions],
  );

  const handleAddTag = useCallback(
    (allSelectedItems: MenuItemProps[] | undefined) => {
      dispatch(
        updateAppointmentDraft({
          selectedAutomationIds: allSelectedItems?.map((item) => item.value || ''),
        }),
      );
    },
    [dispatch],
  );

  const handleRemoveTag = useCallback(
    (tagId) => {
      dispatch(
        updateAppointmentDraft({
          selectedAutomationIds: selectedAutomationIds?.filter((id) => id !== tagId),
        }),
      );
    },
    [selectedAutomationIds, dispatch],
  );

  const handleClearAllTags = useCallback(() => {
    dispatch(
      updateAppointmentDraft({
        selectedAutomationIds: [],
      }),
    );
  }, [dispatch]);

  return (
    <Box className="AppointmentModal__Automations" w="100%">
      <Text as="p" fontWeight="bold" mb={2}>
        Automations
      </Text>
      <TagBlock
        tags={selectedAutomationTags || []}
        onRemoveTag={handleRemoveTag}
        onClearAll={handleClearAllTags}
        isLoading={isLoadingLinkedAutomations}
        dataTestId="appointment-automations"
        menuListProps={{
          isMultiSelect: true,
          isSearchable: true,
          canSelectAll: true,
          onSelectAll: (selectedItems): void => handleAddTag(selectedItems),
          onSelect: (selectedItem, allSelectedItems): void => handleAddTag(allSelectedItems),
          menuItems: automationOptions,
          maxH: '300px',
        }}
      />
    </Box>
  );
};
